/* Copyright (c) 2020-present CloudIO Inc */

import clsx from 'clsx';
import { DataGrid } from 'componentregistry';
import { CellComponentProps } from 'components/DataGrid/grid/types';
import { useCellStyles } from 'components/DataGrid/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { stopPropagation } from 'utils/events';
import useTrigger from '../../../../triggers/useTrigger';
import { useTooltipProps } from 'componentregistry/utils/commonProps';
import { formatColumnValue } from 'components/CanvasGrid/cells/utils';
import { isEmpty } from 'utils/isEmpty';
import Icon from 'icons/Icon';
import { useStateXAtomSetter } from '@cloudio-saas/statex';
import { dataGridSelectedPath } from 'layout/Sidebar/atoms';

export function validate(
  column: Required<DataGrid.StringColumn.StringColumn>,
  _value: string,
): string | null {
  const { minLength, maxLength, regex, label } = column;
  if (_value) {
    if (_value.length < minLength) {
      return `${label.value} must be atleast ${minLength} chars long!`;
    }
    if (_value.length > maxLength) {
      return `${label.value} must be atmost ${maxLength} chars long!`;
    }
    if (_value && regex && !new RegExp(regex).test(_value)) {
      return `Value for ${label.value} must match the regular expression ${regex}`;
    }
  }
  return null;
}

export function StringColumnCell({
  active,
  appUid,
  className,
  column,
  columnData,
  editing,
  height,
  pageId,
  platform,
  roleUid,
  rowIndex,
  width,
}: CellComponentProps<DataGrid.StringColumn.StringColumnItem>): React.ReactElement {
  const { setValue, recordId, store, controller, theme, value, columnIndex } =
    columnData;
  const [localVal, setLocalVal] = useState(value);
  const classes = useCellStyles();
  const ref = useRef<HTMLInputElement>(null);
  const setdataGridSelectedPath = useStateXAtomSetter(dataGridSelectedPath);

  const focusedValue = useRef(localVal);
  const currentValue = useRef(localVal);
  const isFocused = useRef(false);
  const { itemId } = column;
  const [onChangeTrigger] = useTrigger(
    { appUid, roleUid, platform, pageId, itemId },
    'TriggerOnChange',
  );

  const [onFocusTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnFocus',
  );

  const [onBlurTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnBlur',
  );

  const [onValueChangeTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnValueChange',
  );

  const [onEnterKeyPress, , triggerOnTab] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnEnterKeyPress',
  );

  const [onDeleteKeyPress] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnDeleteKeyPress',
  );

  const [afterServerValidateTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerAfterServerValidate',
  );

  const { viewAttribute, formatFunction, alignment, serverValidateOnChange } =
    column.props;

  const onFocus = () => {
    isFocused.current = true;
    focusedValue.current = localVal;
    onFocusTrigger?.();
  };

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    setdataGridSelectedPath({
      dragFromCell: {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
      },
      dragToCell: {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
      },
      dragging: false,
    });
    stopPropagation(e);
  };

  const onBlur = useCallback(async () => {
    isFocused.current = false;
    onBlurTrigger?.();
    if (focusedValue.current !== currentValue.current) {
      onValueChangeTrigger?.();
      if (serverValidateOnChange && store) {
        await store.serverValidate({
          attribute: viewAttribute,
          oldValue: focusedValue.current,
        });
        await afterServerValidateTrigger?.();
      }
    }
  }, [
    onBlurTrigger,
    onValueChangeTrigger,
    afterServerValidateTrigger,
    serverValidateOnChange,
    store,
    viewAttribute,
  ]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      onBlur();
    }
    if (e.key === 'Enter' || (triggerOnTab && e.key === 'Tab')) {
      onEnterKeyPress?.();
    }
    if (e.key === 'Backspace') {
      onDeleteKeyPress?.();
    }
  };
  const placeholder = column.props?.placeholder || '';
  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  useEffect(() => {
    if (ref.current && editing && active) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing, active]);

  useEffect(() => {
    return () => {
      if (isFocused.current) onBlur();
    };
  }, [onBlur]);

  const tipValue = useTooltipProps({
    appUid,
    pageId,
    itemId,
    ...column.props,
  });

  const cn = clsx(className, classes.cell);

  let val = (localVal as string) ?? '';
  if (typeof val === 'object' && !editing) {
    val = JSON.stringify(val);
  }
  let content: React.ReactNode = val;
  if (editing) {
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const _val = e.target.value;
      const errMsg = validate(column.props, _val);
      const currentRecordId = store?.getCurrentRecordId();
      if (errMsg && currentRecordId) {
        store?.setError(viewAttribute, currentRecordId, errMsg);
      } else if (
        currentRecordId &&
        store?.hasError(viewAttribute, currentRecordId)
      ) {
        store?.clearError(viewAttribute, currentRecordId);
      }
      currentValue.current = _val;
      setLocalVal(_val);
      setValue(_val);
      onChangeTrigger?.();
      if (platform.isRecording()) {
        platform.recordLine(
          column.itemId,
          `await ui.type('${column.itemId}', '${_val}' , {row : ${rowIndex}});`,
        );
      }
    };

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
      <div
        className={cn}
        data-grid-editor
        onClick={onClick}
        role="cell"
        style={{ width, height }}
      >
        <input
          ref={ref}
          className={classes.input}
          onChange={onChange}
          style={{ height, lineHeight: height }}
          type="text"
          value={localVal || ''}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
        />
      </div>
    );
  }

  const tip = val.length * 7 > width ? val : undefined;

  let icon = null;
  if (controller && !isEmpty(formatFunction)) {
    const result = formatColumnValue({
      controller,
      formatFunction,
      viewAttribute,
      rawValue: localVal,
      record: store?.getRecord(recordId),
      pageId,
      column,
      platform,
      store,
      row: rowIndex - 1,
      appUid,
      theme,
    });
    if (typeof result === 'string') {
      content = result;
    } else if ('value' in result) {
      content = result.value;
      icon = result.icon;
    }
  }

  return (
    <div
      {...tipValue}
      data-tip={tipValue ? tipValue['data-tip'] : tip}
      data-tip-width={tipValue ? tipValue['data-tip-width'] : 900}
      tabIndex={-1}
      ref={ref}
      className={cn}
      style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: alignment,
      }}
    >
      {icon ? (
        <Icon
          sx={{ mr: '8px' }}
          parentColor={icon.color}
          iconName={icon.iconName}
          iconType={icon.iconType}
        />
      ) : null}
      {content}
    </div>
  );
}
